import { api } from '../../services/api'
import { apiError } from '../../utils/api'
import { MARKETING_CONTENT_ACTIONS } from '../../utils/marketing'
import { showError, showSuccess } from '../misc/misc.actions'

/**
 * Marketing content is its own collection (one doc per facility per item) and arrives on the
 * facility payload as `marketing_content`. These endpoints use the { data, errors, status }
 * envelope — record is `response.data.data`, message is `response.data.errors[0]` — unlike the
 * bare-body facility endpoints in facilities.actions.js. There is no GET: every write returns
 * the record it touched, so the page updates its own state instead of refetching.
 */

const base = (facilityId) => `/facilities/${facilityId}/marketingContent`

export const createMarketingContent = (facilityId, record) => {
	return async (dispatch) => {
		dispatch({ type: MARKETING_CONTENT_ACTIONS.SAVING })

		try {
			const response = await api.post(base(facilityId), record)
			const created = response.data.data

			dispatch({ type: MARKETING_CONTENT_ACTIONS.SAVED, payload: created })
			dispatch(showSuccess('Featured content created!'))

			return created
		} catch (error) {
			const message = apiError(error, 'An error occurred while creating the record.')

			dispatch({ type: MARKETING_CONTENT_ACTIONS.FAIL, payload: message })
			dispatch(showError(message))

			return null
		}
	}
}

export const updateMarketingContent = (facilityId, id, updates) => {
	return async (dispatch) => {
		dispatch({ type: MARKETING_CONTENT_ACTIONS.SAVING })

		try {
			const response = await api.patch(`${base(facilityId)}/${id}`, updates)
			const updated = response.data.data

			dispatch({ type: MARKETING_CONTENT_ACTIONS.SAVED, payload: updated })
			dispatch(showSuccess('Featured content updated!'))

			return updated
		} catch (error) {
			const message = apiError(error, 'An error occurred while updating the record.')

			dispatch({ type: MARKETING_CONTENT_ACTIONS.FAIL, payload: message })
			dispatch(showError(message))

			return null
		}
	}
}

/** Soft delete — the record is retained, just hidden from the admin. */
export const deleteMarketingContent = (facilityId, id) => {
	return async (dispatch) => {
		dispatch({ type: MARKETING_CONTENT_ACTIONS.SAVING })

		try {
			const response = await api.delete(`${base(facilityId)}/${id}`)
			const deleted = response.data.data

			dispatch({ type: MARKETING_CONTENT_ACTIONS.DELETED, payload: deleted })
			dispatch(showSuccess('Featured content removed!'))

			return deleted
		} catch (error) {
			const message = apiError(error, 'An error occurred while removing the record.')

			dispatch({ type: MARKETING_CONTENT_ACTIONS.FAIL, payload: message })
			dispatch(showError(message))

			return null
		}
	}
}

/**
 * Reorders a whole category at once. `ordered_ids` must be every active, non-deleted record in
 * it — the API rejects a partial list. Returns the reordered collection.
 */
export const reorderMarketingContent = (facilityId, category, orderedIds) => {
	return async (dispatch) => {
		dispatch({ type: MARKETING_CONTENT_ACTIONS.SAVING })

		try {
			const response = await api.patch(`${base(facilityId)}/reorder`, {
				category,
				ordered_ids: orderedIds
			})
			const reordered = response.data.data

			dispatch({ type: MARKETING_CONTENT_ACTIONS.REORDERED, payload: { category, reordered } })

			return reordered
		} catch (error) {
			const message = apiError(error, 'An error occurred while reordering.')

			dispatch({ type: MARKETING_CONTENT_ACTIONS.FAIL, payload: message })
			dispatch(showError(message))

			return null
		}
	}
}

/**
 * Uploads a file to S3 and returns its url without persisting it — the caller picks the field
 * (hero artwork or partner logo) and saves the record, so cancelling leaves no orphaned change.
 */
export const uploadMarketingContentImage = (facilityId, file) => {
	return async (dispatch) => {
		const formData = new FormData()
		formData.append('marketing_content_image', file)

		try {
			const response = await api.post(`${base(facilityId)}/image`, formData)

			return response.data.data.url
		} catch (error) {
			const message = apiError(error, 'An error occurred while uploading the file.')

			dispatch(showError(message))

			return null
		}
	}
}
